import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  getCanvasFeatures,
  getCanvasFeaturesVariables,
} from 'benchling-apps/interactivity/note-linked-canvas/__generated/getCanvasFeatures';
import * as benchlingUtil from 'BenchlingUtil';
import {BenchlingAppCanvasLocation} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import BenchLink from 'link-preview/BenchLink';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import * as Colors from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';
import {Title} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Link from 'ui-library/Link';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
type Feature = getCanvasFeatures['benchlingAppCanvasFeaturesForLocation']['canvasFeatures'][number];

interface InsertNoteLinkedCanvasModalProps {
  onClose: () => void;
  onSubmit: (appInstallationApiId: string, featureId: string) => void;
}

export const FEATURES_QUERY = gql`
  query getCanvasFeatures($location: BenchlingAppCanvasLocation!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingAppCanvasFeaturesForLocation(location: $location) {
      canvasFeatures {
        ...BenchlingAppFeatureGraphQL
      }
    }
  }
  ${getFragmentWrapper('BenchlingAppFeature').fragment}
`;

const InsertNoteLinkedCanvasModal = ({onClose, onSubmit}: InsertNoteLinkedCanvasModalProps): JSX.Element => {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const submitHandler = (_: unknown): void => {
    if (selectedFeature) {
      onSubmit(selectedFeature.benchlingApp.id, selectedFeature.userDefinedId);
    }
  };
  const MODAL_FOOTER: Array<ModalButton> = [
    {text: 'Close', onClick: onClose, cancel: true},
    {
      text: 'Insert',
      onClick: submitHandler,
      submit: true,
      style: 'primary',
      disabled: selectedFeature == null,
      tooltipTitle: selectedFeature == null ? 'No canvas selected' : null,
      dataTestAttrs: {component: 'InsertNoteLinkedCanvasModal', key: ModalButtonKey.INSERT},
    },
  ];

  return (
    <BenchlingQuery<getCanvasFeatures, getCanvasFeaturesVariables>
      query={FEATURES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{location: BenchlingAppCanvasLocation.ENTRY}}
    >
      {(data) => {
        const features = data.benchlingAppCanvasFeaturesForLocation.canvasFeatures;
        return (
          <Modal onRequestClose={onClose} buttons={MODAL_FOOTER} title="Insert Canvas">
            {features.length === 0 ? (
              <div className={css(styles.emptyContainer)}>
                <div className={css(styles.infoIcon)}>
                  <BenchlingIcon icon="info-circle" />
                </div>
                <Title>No canvases available yet.</Title>
                <p>
                  Canvases allow you to interact with custom Benchling apps directly from your entry. Contact
                  your admin to get set up.{' '}
                  <Link href="https://docs.benchling.com/docs/introduction-to-app-canvas">Learn more</Link>.
                </p>
              </div>
            ) : (
              <div className={css(styles.featuresContainer)}>
                {features.map((feat, i) => (
                  <FeatureSelector
                    key={i}
                    selectedFeature={selectedFeature}
                    setSelectedFeature={setSelectedFeature}
                    feature={feat}
                    dataTestAttrs={{component: 'FeatureSelector', key: feat.name}}
                  />
                ))}
              </div>
            )}
          </Modal>
        );
      }}
    </BenchlingQuery>
  );
};

interface FeatureSelectorProps {
  selectedFeature: Feature | null;
  setSelectedFeature: React.Dispatch<React.SetStateAction<Feature | null>>;
  feature: Feature;
  dataTestAttrs: DataTestAttrs;
}

export function FeatureSelector({
  selectedFeature,
  setSelectedFeature,
  feature,
  dataTestAttrs,
}: FeatureSelectorProps): JSX.Element {
  const currentFeatureIsSelected = selectedFeature?.id === feature.id;

  return (
    <div
      className={css(styles.featureButton)}
      onClick={() => {
        if (currentFeatureIsSelected) {
          setSelectedFeature(null);
        } else {
          setSelectedFeature(feature);
        }
      }}
      {...getTestProps(dataTestAttrs)}
    >
      <div className={css(styles.featureContent, currentFeatureIsSelected ? styles.selected : null)}>
        <div className={css(styles.inlineBlock, styles.featureIcon)}>
          <BenchlingIcon icon="plug" color={Colors.GRAY_DARK} />
        </div>
        <div className={css(styles.inlineBlock)}>
          <Header2>{feature.name}</Header2>
          From app{' '}
          <BenchLink
            href={benchlingUtil.pathToURL(`/apps/${feature.benchlingApp.id}`)}
            className={css(styles.bold)}
          >
            {feature.benchlingApp.name}
          </BenchLink>
        </div>
      </div>
    </div>
  );
}

export default InsertNoteLinkedCanvasModal;

const styles = StyleSheet.create({
  bold: {
    fontWeight: 'bold',
  },
  featuresContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  featureButton: {
    flexBasis: '50%',
  },
  featureContent: {
    ':hover': {
      cursor: 'pointer',
      backgroundColor: Colors.GRAY_LIGHTER,
      border: `solid 1px ${Colors.GRAY}`,
    },
    borderRadius: '4px',
    margin: '10px',
    border: `solid 1px ${Colors.GRAY_LIGHT}`,
    padding: '10px',
  },
  featureIcon: {
    paddingTop: '4px',
    paddingRight: '10px',
  },
  inlineBlock: {
    verticalAlign: 'top',
    display: 'inline-block',
  },
  selected: {
    border: `solid 1px ${Colors.GRAY}`,
    backgroundColor: Colors.GRAY_LIGHTER,
  },
  emptyContainer: {
    textAlign: 'center',
    maxWidth: '300px',
    justifySelf: 'center',
    padding: '32px 0',
  },
  infoIcon: {
    fontSize: '24px',
    color: Colors.GRAY_SEMIDARK,
    margin: '4px 0',
  },
});
